import { createTheme, alpha } from '@mui/material/styles';
import { DEFAULT_USER_COLOR, TRANSITIONS, Z_INDEX } from './theme/constants';

/**
 * Base theme configuration shared between light and dark modes
 */
const getBaseTheme = () => ({
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    button: { fontWeight: 600 },
  },
});

/**
 * Light mode palette
 */
const getLightPalette = () => ({
  mode: 'light',
  primary: {
    main: '#00498a',
    light: '#3671aa',
    dark: '#003360',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#d51130',
    light: '#dd4356',
    dark: '#a50d25',
    contrastText: '#ffffff',
  },
  background: {
    default: '#f8f9fa',
    paper: '#ffffff',
  },
  text: {
    primary: '#212529',
    secondary: '#495057',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  error: {
    main: '#d51130',
    light: '#dd4356',
    dark: '#a50d25',
    contrastText: '#ffffff',
  },
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#ffffff',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
    contrastText: '#ffffff',
  },
  action: {
    active: 'rgba(0, 73, 138, 0.54)',
    hover: 'rgba(0, 73, 138, 0.04)',
    selected: 'rgba(0, 73, 138, 0.08)',
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    focus: 'rgba(0, 73, 138, 0.12)',
  },
  divider: 'rgba(0, 73, 138, 0.12)',
});

/**
 * Dark mode palette (Material Design 3 inspired)
 */
const getDarkPalette = () => ({
  mode: 'dark',
  primary: {
    main: '#4a90e2',      // Lighter blue for better contrast
    light: '#73a9ed',
    dark: '#2667b8',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#ef5777',      // Softer red for dark backgrounds
    light: '#f37992',
    dark: '#d93557',
    contrastText: '#ffffff',
  },
  background: {
    default: '#0a0e14',   // Very dark blue-gray
    paper: '#141b24',     // Slightly lighter for cards
  },
  text: {
    primary: '#e4e6eb',   // Off-white for reduced eye strain
    secondary: '#b0b3b8', // Medium gray
    disabled: 'rgba(255, 255, 255, 0.38)',
  },
  error: {
    main: '#ef5777',
    light: '#f37992',
    dark: '#d93557',
    contrastText: '#ffffff',
  },
  success: {
    main: '#66bb6a',
    light: '#81c784',
    dark: '#4caf50',
    contrastText: '#000000',
  },
  warning: {
    main: '#ffa726',
    light: '#ffb74d',
    dark: '#ff9800',
    contrastText: '#000000',
  },
  info: {
    main: '#42a5f5',
    light: '#64b5f6',
    dark: '#2196f3',
    contrastText: '#000000',
  },
  action: {
    active: 'rgba(74, 144, 226, 0.54)',
    hover: 'rgba(74, 144, 226, 0.08)',
    selected: 'rgba(74, 144, 226, 0.16)',
    disabled: 'rgba(255, 255, 255, 0.26)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
    focus: 'rgba(74, 144, 226, 0.12)',
  },
  divider: 'rgba(74, 144, 226, 0.12)',
});

/**
 * Component overrides that adapt to theme mode
 */
const getComponents = (mode) => {
  const isLight = mode === 'light';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: 'background-color 0.3s ease, color 0.3s ease',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          textTransform: 'none',
          boxShadow: 'none',
          minHeight: 42,
          fontSize: '0.875rem',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: isLight
              ? '0 4px 14px 0 rgba(0, 73, 138, 0.15)'
              : '0 4px 14px 0 rgba(74, 144, 226, 0.25)',
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.text.disabled,
            opacity: 0.6,
          },
          [theme.breakpoints.down('sm')]: {
            minHeight: 36,
            fontSize: '0.8125rem',
          },
        }),
        contained: ({ theme }) => ({
          background: isLight
            ? 'linear-gradient(135deg, #00498a 0%, #003360 100%)'
            : 'linear-gradient(135deg, #4a90e2 0%, #2667b8 100%)',
          '&.MuiButton-containedSecondary': {
            background: isLight
              ? 'linear-gradient(135deg, #d51130 0%, #a50d25 100%)'
              : 'linear-gradient(135deg, #ef5777 0%, #d93557 100%)',
          },
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
        }),
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
      variants: [
        {
          props: { variant: 'soft', color: 'primary' },
          style: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
          }),
        },
        {
          props: { variant: 'soft', color: 'secondary' },
          style: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            color: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
            },
          }),
        },
      ],
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          paddingLeft: theme.spacing(0.5),
          paddingRight: theme.spacing(0.5),
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }),
        shrink: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: isLight
            ? 'linear-gradient(135deg, rgba(0, 73, 138, 0.02) 0%, rgba(213, 17, 48, 0.02) 100%)'
            : 'linear-gradient(135deg, rgba(74, 144, 226, 0.03) 0%, rgba(239, 87, 119, 0.03) 100%)',
          borderRadius: 10,
          boxShadow: isLight
            ? '0 2px 10px 0 rgba(0, 0, 0, 0.05)'
            : '0 2px 10px 0 rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s ease',
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            background: isLight
              ? 'rgba(255, 255, 255, 0.8)'
              : 'rgba(20, 27, 36, 0.6)',
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
            },
          },
          '& .MuiOutlinedInput-input': {
            [theme.breakpoints.down('sm')]: {
              fontSize: 16,
            },
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: isLight
            ? 'linear-gradient(90deg, #00498a 0%, #005aa8 100%)'
            : 'linear-gradient(90deg, #141b24 0%, #1a2332 100%)',
          boxShadow: isLight
            ? '0 2px 10px rgba(0, 0, 0, 0.1)'
            : '0 2px 10px rgba(0, 0, 0, 0.5)',
          zIndex: 1200,
          borderRadius: 0,
          transition: 'all 0.3s ease',
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: isLight
            ? '0 4px 20px rgba(0, 0, 0, 0.05)'
            : '0 4px 20px rgba(0, 0, 0, 0.3)',
          border: `1px solid ${alpha(theme.palette.primary.main, isLight ? 0.08 : 0.15)}`,
          transition: 'all 0.3s ease',
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiChip-colorPrimary': {
            backgroundColor: alpha(theme.palette.primary.main, isLight ? 0.1 : 0.2),
            color: theme.palette.primary.main,
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: alpha(theme.palette.secondary.main, isLight ? 0.1 : 0.2),
            color: theme.palette.secondary.main,
          },
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '@media (min-width: 600px)': {
            minHeight: '64px',
          },
          transition: 'min-height 0.3s ease',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          transition: 'background-color 0.3s ease',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          margin: 32,
          maxWidth: 900,
          [theme.breakpoints.down('sm')]: {
            margin: 16,
            maxWidth: 'calc(100vw - 32px)',
          },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(3),
          gap: theme.spacing(1),
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            flexDirection: 'column-reverse',
            gap: theme.spacing(1),
            '& > :not(:first-of-type)': {
              marginLeft: 0,
            },
            '& > *': {
              width: '100%',
            },
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingBottom: theme.spacing(1),
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        head: ({ theme }) => ({
          backgroundColor: alpha(
            theme.palette.primary.main,
            isLight ? 0.05 : 0.1
          ),
          fontWeight: 600,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
          },
          '&:hover': {
            backgroundColor: theme.palette.action.selected,
          },
        }),
      },
    },
  };
};

/**
 * Create custom shadows array based on theme mode
 * @param {'light' | 'dark'} mode - The theme mode
 */
const getCustomShadows = (mode) => {
  const isLight = mode === 'light';
  const baseColor = isLight ? '0, 0, 0' : '0, 0, 0';
  const opacity = isLight ? 1 : 1.5;

  return [
    'none',
    `0 1px 3px rgba(${baseColor}, ${0.05 * opacity})`,
    `0 2px 8px rgba(${baseColor}, ${0.08 * opacity})`,
    `0 4px 14px rgba(${baseColor}, ${0.12 * opacity})`,
    `0 6px 20px rgba(${baseColor}, ${0.15 * opacity})`,
    `0 8px 24px rgba(${baseColor}, ${0.18 * opacity})`,
    `0 10px 28px rgba(${baseColor}, ${0.20 * opacity})`,
    `0 12px 32px rgba(${baseColor}, ${0.22 * opacity})`,
    `0 14px 36px rgba(${baseColor}, ${0.24 * opacity})`,
    `0 16px 40px rgba(${baseColor}, ${0.26 * opacity})`,
    `0 18px 44px rgba(${baseColor}, ${0.28 * opacity})`,
    `0 20px 48px rgba(${baseColor}, ${0.30 * opacity})`,
    `0 22px 52px rgba(${baseColor}, ${0.32 * opacity})`,
    `0 24px 56px rgba(${baseColor}, ${0.34 * opacity})`,
    `0 26px 60px rgba(${baseColor}, ${0.36 * opacity})`,
    `0 28px 64px rgba(${baseColor}, ${0.38 * opacity})`,
    `0 30px 68px rgba(${baseColor}, ${0.40 * opacity})`,
    `0 32px 72px rgba(${baseColor}, ${0.42 * opacity})`,
    `0 34px 76px rgba(${baseColor}, ${0.44 * opacity})`,
    `0 36px 80px rgba(${baseColor}, ${0.46 * opacity})`,
    `0 38px 84px rgba(${baseColor}, ${0.48 * opacity})`,
    `0 40px 88px rgba(${baseColor}, ${0.50 * opacity})`,
    `0 42px 92px rgba(${baseColor}, ${0.52 * opacity})`,
    `0 44px 96px rgba(${baseColor}, ${0.54 * opacity})`,
    `0 46px 100px rgba(${baseColor}, ${0.56 * opacity})`,
  ];
};

/**
 * Create theme for the given mode
 * @param {'light' | 'dark'} mode - The theme mode
 */
export const createAppTheme = (mode = 'light') => {
  const palette = mode === 'light' ? getLightPalette() : getDarkPalette();
  const isLight = mode === 'light';

  const theme = createTheme({
    ...getBaseTheme(),
    palette,
    shadows: getCustomShadows(mode),
    components: getComponents(mode),
  });

  // Add custom theme tokens
  return {
    ...theme,

    // Custom gradients
    gradients: {
      primary: `linear-gradient(90deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
      secondary: `linear-gradient(90deg, ${palette.secondary.main} 0%, ${palette.secondary.dark} 100%)`,
      light: isLight
        ? 'linear-gradient(90deg, #e2e8f0, #ffffff)'
        : 'linear-gradient(90deg, #1e293b, #334155)',
    },

    // Custom borders
    borders: {
      thin: `1px solid ${palette.divider}`,
      medium: `2px solid ${palette.divider}`,
      thick: `3px solid ${palette.divider}`,
      dashed: `1px dashed ${alpha(palette.text.primary, 0.2)}`,
    },

    // User defaults
    userDefaults: {
      color: DEFAULT_USER_COLOR,
    },

    // Z-index values (also available via constants)
    zIndex: {
      ...theme.zIndex,
      ...Z_INDEX,
    },
  };
};

// Default export for backwards compatibility (will be replaced by ThemeContext)
export default createAppTheme('light');
